import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NotificationLevel } from '../../../models/enums/notificationlevel.enum';
import { TriggerQueueState } from '../../../models/enums/triggerqueuestate.enum';
import { MonitoringService } from '../../../services/monitoring.service';
import { SchedulerService } from '../../../services/scheduler.service';
import { WebsocketService } from '../../../services/websocket.service';
import { JobInfoDialog } from './job.info.dialog';

@Component({
    selector: 'jobs-queue',
    templateUrl: './jobs.queue.html',
    styleUrls: ['./jobs.queue.css']
})
export class JobsQueueComponent implements OnInit, OnDestroy {
    //#region AllUsers
    AllUsersValue = false;

    @Input()
    get AllUsers() {
        return this.AllUsersValue;
    }
    set AllUsers(val) {
        this.AllUsersValue = val;
        this.AllUsersChange.emit(this.AllUsersValue);
    }

    @Output() AllUsersChange = new EventEmitter<any>();
    //#endregion

    //#region ShowAllUsers
    ShowAllUsersValue = true;

    @Input()
    get ShowAllUsers() {
        return this.ShowAllUsersValue;
    }
    set ShowAllUsers(val) {
        this.ShowAllUsersValue = val;
        this.ShowAllUsersChange.emit(this.ShowAllUsersValue);
    }

    @Output() ShowAllUsersChange = new EventEmitter<any>();
    //#endregion

    Rows;
    TriggerStates = TriggerQueueState;
    MessageSubscription;

    /**
     * Das unveraenderte Ergebnis vom Server, nach Trigger gruppiert. Die Anzeige entsteht
     * daraus in Build().
     *
     * Getrennt gehalten, damit ein Wechsel des Filters keinen neuen Serveraufruf braucht --
     * die Liste aktualisiert sich ohnehin schon bei jedem TriggerUpdate.
     */
    Groups;

    /** 'all' | 'service' | 'worker' -- siehe Build(). */
    RunFilter = 'all';


    /**
     * Wie viele Workflows im Dienst selbst gestartet sind.
     *
     * Diese Laeufe tauchen im Job-Protokoll nirgends auf: Dort steht nur, was ein Worker
     * ausgefuehrt hat. Die Zahl gilt je Replica -- sie kommt aus dem Prozess, der die Anfrage
     * beantwortet hat, und ist bei mehreren Replicas ein Ausschnitt, keine Summe.
     */
    ServiceCounts = null;

    constructor(private service: SchedulerService, private monitoring: MonitoringService, private cdRef: ChangeDetectorRef) {
    }
    ngOnInit(): void {
        this.Refresh();
        this.MessageSubscription = WebsocketService.MessageReceived.subscribe((response) => {
            const parsed = JSON.parse(response);
            if (parsed.Level === NotificationLevel.System && parsed.Type === 'TriggerUpdate') {
                this.Refresh();
            }
        });
        WebsocketService.Subscribe([{ Channel: 'TriggerUpdate', Level: [0] }]);
    }
    ngOnDestroy(): void {
        this.MessageSubscription.unsubscribe();
        WebsocketService.Unsubscribe([{ Channel: 'TriggerUpdate', Level: [0] }]);
    }

    Refresh() {
        // Zusammen mit der Warteschlange holen: Beides beschreibt denselben Augenblick.
        this.monitoring.ServiceWorkflowCounts().subscribe({
            next: (result) => { this.ServiceCounts = result; this.cdRef.detectChanges(); },
            // Beiwerk: Faellt die Zahl aus, bleibt die Zeile leer und die Liste laeuft weiter.
            error: () => { this.ServiceCounts = null; }
        });
        this.Rows = null;
        this.service.GetTriggerStateInfos(this.AllUsersValue).subscribe(result => {
            this.Groups = result;
            this.Build();
            this.cdRef.detectChanges();
        });
    }

    /**
     * Baut aus den Gruppen die flache Zeilenliste, die die Tabelle anzeigt.
     *
     * Bewusst eine Methode und kein Getter: Ein Getter im *ngFor liefert bei jeder
     * Pruefung ein neues Array, und Angular vergleicht ueber die Referenz -- die Tabelle
     * wuerde sich dann dauernd neu aufbauen.
     */
    Build() {
        this.Rows = null;
        if (!this.Groups || this.Groups.length === 0) {
            return;
        }
        const rows = [];
        this.Groups.forEach(x => {
            const runs = (x.RunInfos || []).filter(ri => this.Matches(ri));
            // Eine Gruppe ohne passenden Lauf nur zeigen, wenn gar nicht gefiltert wird. Sonst
            // bliebe bei "nur im Dienst" eine Liste leerer Trigger-Zeilen stehen.
            if (this.RunFilter !== 'all' && runs.length === 0) {
                return;
            }
            x.ChildRow = false;
            x.DisplayName = x.TriggerName;
            rows.push(x);
            runs.forEach(ri => {
                ri.ChildRow = true;
                // Der Name kommt vom Server und nennt bei Unterlaeufen auch den
                // aufrufenden Workflow. Fehlt er bei alten Eintraegen, bleibt die Lauf-ID
                // das Einzige, was den Lauf benennt.
                ri.DisplayName = ri.Name ? ri.Name : ri.RunID;
                rows.push(ri);
            });
        });
        if (rows.length > 0) {
            this.Rows = rows;
        }
    }

    /** Ob ein Lauf zum gewaehlten Filter passt. */
    Matches(run): boolean {
        if (this.RunFilter === 'service') {
            return run.RunsInService === true;
        }
        if (this.RunFilter === 'worker') {
            return run.RunsInService !== true;
        }
        return true;
    }

    /** Filterwechsel kommt ohne Serveraufruf aus -- die Gruppen liegen schon da. */
    FilterChanged() {
        this.Build();
        this.cdRef.detectChanges();
    }

    GetTaskInfo(row) {
        JobInfoDialog.ShowDialog(row.RunID);
    }

    CancelTask(row) {
        this.service.CancelTrigger(row.RunID).subscribe();
    }
}
